// Angular
import { Injectable } from '@angular/core';

/**
 * One selectable language row
 */
export interface ILanguageOption {
  code: string;
  label: string;
}

// AccountPreferences field persisted when the user picks a language
export const LANGUAGE_PREFERENCE_FIELD = 'Language';

// Used whenever the stored/browser locale is not one we ship
export const DEFAULT_LANGUAGE_CODE = 'en';

/**
 * Pure business logic for the Language component.
 * The selectable list mirrors the legacy `languages` map in frontend/js/user.js.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  readonly languages: ILanguageOption[] = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'French' },
    { code: 'ja', label: 'Japanese' },
    { code: 'pt', label: 'Portuguese' },
    { code: 'es', label: 'Spanish' },
    { code: 'tagalog', label: 'Tagalog' }
  ];

  /**
   * True when the code maps to one of the selectable languages
   * @param {string | null | undefined} code Language code to test
   * @returns {boolean} True when the language is selectable
   */
  isSupported(code?: string | null): boolean {
    if (!code) return false;
    return this.languages.some((language) => language.code === code.toLowerCase());
  }

  /**
   * Coerce any code to a selectable one, falling back to English
   * @param {string | null | undefined} code Language code to normalize
   * @returns {string} Selectable language code
   */
  normalize(code?: string | null): string {
    if (!this.isSupported(code)) return DEFAULT_LANGUAGE_CODE;
    return (code as string).toLowerCase();
  }

  /**
   * Language a session should run in: the stored choice while the user is
   * signed in, English once signed out
   * @param {boolean} isAuthenticated True while a user is signed in
   * @param {string | null | undefined} stored Language kept for the session
   * @returns {string} Selectable language code
   */
  sessionLanguage(isAuthenticated: boolean, stored?: string | null): string {
    if (!isAuthenticated) return DEFAULT_LANGUAGE_CODE;
    return this.normalize(stored);
  }

  /**
   * First selectable code among the candidates, else the browser language
   * @param {(string | null | undefined)[]} candidates Codes in priority order
   * @returns {string} Selectable language code
   */
  resolve(...candidates: (string | null | undefined)[]): string {
    const match = candidates.find((code) => this.isSupported(code));
    return match ? this.normalize(match) : this.browserLanguage();
  }

  /**
   * Browser locale when we ship it, otherwise English
   * @returns {string} Selectable language code
   */
  browserLanguage(): string {
    if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE_CODE;
    return this.normalize((navigator.language ?? '').split('-')[0]);
  }

  /**
   * Display label for a language code
   * @param {string} code Language code
   * @returns {string} Display label, or '' when unknown
   */
  labelOf(code: string): string {
    return this.languages.find((language) => language.code === code)?.label ?? '';
  }
}
